import Quill from 'quill';

type QuillBlot = {
  statics?: { blotName?: string };
  prev?: QuillBlot | null;
  next?: QuillBlot | null;
  domNode?: HTMLElement;
  offset: (scroll: unknown) => number;
  length: () => number;
};

const isDividerBlot = (blot: QuillBlot | null | undefined): boolean =>
  blot?.statics?.blotName === 'divider';

/** 구분선 삽입 — 다음 블록이 있으면 빈 줄 추가 없이 커서만 이동 */
export const insertQuillDivider = (quill: Quill) => {
  const range = quill.getSelection(true);
  if (!range) return;

  let index = range.index;
  if (range.length > 0) {
    quill.deleteText(index, range.length, 'user');
  }

  const [line, offset] = quill.getLine(index);
  if (line && offset > 0) {
    quill.insertText(index, '\n', 'user');
    index += 1;
  }

  quill.insertEmbed(index, 'divider', true, 'user');

  const afterIndex = index + 1;
  const atDocEnd = afterIndex >= quill.getLength() - 1;
  if (atDocEnd) {
    quill.insertText(afterIndex, '\n', 'user');
    quill.setSelection(afterIndex + 1, 'user');
  } else {
    quill.setSelection(afterIndex, 'user');
  }
};

const deleteDividerBlot = (quill: Quill, divider: QuillBlot, rangeIndex: number) => {
  const dividerIndex = divider.offset(quill.scroll);
  quill.deleteText(dividerIndex, 1, 'user');
  const nextIndex = Math.min(
    rangeIndex > dividerIndex ? rangeIndex - 1 : rangeIndex,
    Math.max(0, quill.getLength() - 1),
  );
  quill.setSelection(nextIndex, 0, 'user');
};

export const DIVIDER_KEYBOARD_BINDINGS = {
  'divider-delete-backspace': {
    key: 'Backspace',
    collapsed: true,
    handler(this: { quill: Quill }, range: { index: number }) {
      const [line, offset] = this.quill.getLine(range.index);
      if (!line) return true;

      if (isDividerBlot(line as QuillBlot)) {
        deleteDividerBlot(this.quill, line as QuillBlot, range.index);
        return false;
      }

      if (offset !== 0) return true;

      const prev = (line as QuillBlot).prev;
      if (!isDividerBlot(prev ?? undefined)) return true;

      deleteDividerBlot(this.quill, prev as QuillBlot, range.index);
      return false;
    },
  },
  'divider-delete-forward': {
    key: 'Delete',
    collapsed: true,
    handler(this: { quill: Quill }, range: { index: number }) {
      const [line, offset] = this.quill.getLine(range.index);
      if (!line) return true;

      const lineBlot = line as QuillBlot;
      const atLineEnd = offset === lineBlot.length() - 1;
      if (!atLineEnd) return true;

      const next = lineBlot.next;
      if (!isDividerBlot(next ?? undefined)) return true;

      deleteDividerBlot(this.quill, next as QuillBlot, range.index);
      return false;
    },
  },
};
